import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  useTSQItemList,
  useTSMDeactivateItem,
  useTSMReactivateItem,
} from "../../hooks/useTSItems";
import { ListRequest, ListRequestFilter } from "../../types/common/commonTypes";
import { ItemFull, ITEM_TYPES } from "../../types/itemTypes";
import { paginationDefaults } from "../../config/settingsDefaults";

export const PageItemList: React.FC = () => {
  const navigate = useNavigate();

  // Local state for UI controls
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [error, setError] = useState<string | null>(null);

  // Sorting
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // TanStack Query hooks
  const {
    data: itemsResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQItemList({
    skip: -1,
    limit: -1,
    active_status: statusFilter,
    filters: searchFilters,
  });

  const deleteItemMutation = useTSMDeactivateItem();
  const restoreItemMutation = useTSMReactivateItem();

  // Extract data from response
  const items = itemsResponse?.items || [];
  const totalCount = itemsResponse?.total || 0;

  // Handle mutations
  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }

    try {
      await deleteItemMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to delete item:", err);
      setError(err.response?.data?.detail || "Failed to delete item");
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await restoreItemMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to restore item:", err);
      setError(err.response?.data?.detail || "Failed to restore item");
    }
  };

  // Pagination handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Get sortable value from item
  const getSortValue = (item: ItemFull, property: string): any => {
    switch (property) {
      case "number":
        return item.number;
      case "name":
        return item.name;
      case "item_type":
        return item.item_type || "";
      case "uom_type":
        return item.uom_type.type_name;
      case "status":
        return item.is_record_active ? "active" : "inactive";
      default:
        return "";
    }
  };

  // Filter and sort items (client-side filtering for search and category)
  const filteredAndSortedItems = useMemo(() => {
    // First filter
    const filtered = items.filter((item) => {
      const matchesSearch =
        searchTerm === "" ||
        item.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === "" ||
        (item.item_type &&
          item.item_type.toLowerCase().includes(categoryFilter.toLowerCase()));

      return matchesSearch && matchesCategory;
    });

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [items, searchTerm, categoryFilter, orderBy, order, pagination]);

  // Get unique categories for filter dropdown
  const uniqueCategories = ITEM_TYPES.filter((a) => true).sort();

  // Show loading spinner only on initial load
  if (isLoading && items.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Items
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/inventory/items/form?mode=create")}
        >
          Add Item
        </Button>
      </Box>

      {/* Error handling */}
      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An error occurred"}
        </Alert>
      )}

      {/* Loading indicator for background updates */}
      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            placeholder="Search by part number or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {uniqueCategories.map((category) => (
                <MenuItem key={category || "unknown"} value={category || ""}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Items Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "number" ? order : false}>
                <TableSortLabel
                  active={orderBy === "number"}
                  direction={orderBy === "number" ? order : "asc"}
                  onClick={() => handleSort("number")}
                >
                  Part Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Item Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "item_type" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "item_type"}
                  direction={orderBy === "item_type" ? order : "asc"}
                  onClick={() => handleSort("item_type")}
                >
                  Item Type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "uom_type" ? order : false}>
                <TableSortLabel
                  active={orderBy === "uom_type"}
                  direction={orderBy === "uom_type" ? order : "asc"}
                  onClick={() => handleSort("uom_type")}
                >
                  UOM Type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No items found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {item.number}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {item.item_type && (
                      <Chip
                        label={item.item_type}
                        size="small"
                        variant="outlined"
                        clickable={false}
                        onClick={() => {}}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.uom_type.type_name}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.is_record_active ? "Active" : "Inactive"}
                      color={item.is_record_active ? "success" : "default"}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/inventory/items/form?mode=view&id=${item.id}`,
                            )
                          }
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit Item">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/inventory/items/form?mode=edit&id=${item.id}`,
                            )
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      {!item.is_record_active ? (
                        <Tooltip title="Restore Item">
                          <IconButton
                            size="small"
                            onClick={() => handleRestore(item.id)}
                            color="success"
                            disabled={restoreItemMutation.isPending}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Delete Item">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item.id)}
                            color="error"
                            disabled={deleteItemMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
